import { type DocumentData, doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { getFirebaseDb } from '../app';
import { getDocument, subscribeToDocument, updateDocument } from '../firestore';

const COLLECTION = 'users';

export async function getUser(userId: string): Promise<(DocumentData & { id: string }) | null> {
  return getDocument(COLLECTION, userId);
}

export async function createUser(uid: string, data: Record<string, unknown>): Promise<void> {
  const db = getFirebaseDb();
  const userRef = doc(db, COLLECTION, uid);
  await setDoc(userRef, {
    uid,
    createdAt: serverTimestamp(),
    ownedOfficeIds: [],
    productTours: {
      onboarding: {
        closed: false,
        finished: false,
        lastStepId: null,
        steps: {},
      },
    },
    ...data,
  });
}

export async function updateUser(userId: string, data: Record<string, unknown>): Promise<void> {
  return updateDocument(COLLECTION, userId, data);
}

export async function terminateAccount(userId: string): Promise<void> {
  return updateDocument(COLLECTION, userId, { isTerminated: true });
}

export function subscribeToUser(
  userId: string,
  callback: (data: (DocumentData & { id: string }) | null) => void,
): () => void {
  return subscribeToDocument(COLLECTION, userId, callback);
}

/**
 * CAB-603 — subscribe to `users/{uid}/limits/current`, the authoritative quota
 * and usage counter.
 *
 * Read-only by security rule; only Cloud Functions (Admin SDK) write it. The
 * callback receives `null` when the document does not exist yet — for users who
 * predate the backfill. Callers must treat that as FREE tier, never unlimited.
 */
export function subscribeToUserLimits(
  userId: string,
  callback: (data: (DocumentData & { id: string }) | null) => void,
): () => void {
  return subscribeToDocument(`${COLLECTION}/${userId}/limits`, 'current', callback);
}

/**
 * CAB-606 — subscribe to a document under `users/{uid}/preferences`.
 *
 * Client-writable by rule, which is exactly why dismissal state lives here and
 * not on `users/{uid}/limits/current`: that document is `allow write: if false`,
 * so a user could never dismiss anything stored in it.
 */
export function subscribeToUserPreference(
  userId: string,
  preferenceId: string,
  callback: (data: (DocumentData & { id: string }) | null) => void,
): () => void {
  return subscribeToDocument(`${COLLECTION}/${userId}/preferences`, preferenceId, callback);
}

/**
 * CAB-606 — write a document under `users/{uid}/preferences`.
 *
 * Merges, so unrelated preference keys in the same document survive.
 */
export async function setUserPreference(
  userId: string,
  preferenceId: string,
  data: Record<string, unknown>,
): Promise<void> {
  const db = getFirebaseDb();
  const ref = doc(db, COLLECTION, userId, 'preferences', preferenceId);
  await setDoc(ref, data, { merge: true });
}
